import { type ReactNode, useState } from 'react'

import {
  Collapsible,
  Descriptions,
  Divider,
  Field,
  Form,
  IconAttention,
  PrimaryButton,
  Space,
  TertiaryButton,
  Text,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { PrimaryCategorySelect } from '@webapp/core/components/PrimaryCategorySelect'
import { SecondaryCategorySelect } from '@webapp/core/components/SecondaryCategorySelect'
import { type CurrencyName } from '@webapp/core/entities/external/Currency'
import { type PrimaryCategory } from '@webapp/core/entities/internal/PrimaryCategory'
import { type SecondaryCategory } from '@webapp/core/entities/internal/SecondaryCategory'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ApprovedExpenseItem } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import {
  type Account,
  AccountSelect,
  type BudgetPeriod,
} from '@webapp/platform/finance'
import messages from './messages'

// Overrides DataTable's `width: fit-content` cell wrapper so the account
// select fills the column (UI-memory "Full-Width Components in DataTable
// Cells").
const FullWidthCellDecoratorStyled = styled.div`
  width: 100%;

  > div {
    display: block;
    width: 100% !important;
  }
`

export interface CorrectExpenseItemAccountCodesFormSchema {
  primaryCategory: Nullable<PrimaryCategory>
  secondaryCategory: Nullable<SecondaryCategory>
  applyToAllAccount: Nullable<Account>
  accountsByItemId: Record<number, Nullable<Account>>
}

export interface ICorrectExpenseItemAccountCodesFormProps {
  item: ApprovedExpenseItem
  siblings: ApprovedExpenseItem[]
  budgetPeriodsByItemId: Record<number, BudgetPeriod | undefined>
  isLoadingSiblings: boolean
  isSaving: boolean
  showBudgetDate: boolean
  showApplyToAllAccount: boolean
  /** Report-level currency code — amounts format with it (BHV-012) */
  currencyCode?: CurrencyName
  /** Resolves an attachment filename to its media URL (Receipt chip) */
  resolveAttachmentUrl: (attachment: string) => string
  onSubmit: (values: CorrectExpenseItemAccountCodesFormSchema) => void
  onCancel: () => void
  onAccountSelected: (expenseItemId: number, accountId: number) => void
}

const getAttachmentFileType = (attachment: string): string =>
  attachment.split('.').pop()?.toUpperCase() ?? ''

export const CorrectExpenseItemAccountCodesForm = ({
  item,
  siblings,
  budgetPeriodsByItemId,
  isLoadingSiblings,
  isSaving,
  showBudgetDate,
  showApplyToAllAccount,
  currencyCode,
  resolveAttachmentUrl,
  onSubmit,
  onCancel,
  onAccountSelected,
}: ICorrectExpenseItemAccountCodesFormProps) => {
  const intl = useProcurifyIntl()

  const [showSiblings, setShowSiblings] = useState(false)

  const rows = [item, ...siblings]

  const validationSchema = Yup.object().shape({
    secondaryCategory: Yup.object()
      .nullable()
      .required(intl.formatMessage(messages.secondaryCategoryRequiredError)),
    accountsByItemId: Yup.object().shape(
      rows.reduce(
        (shape, row) => ({
          ...shape,
          [row.id]: Yup.object()
            .nullable()
            .required(intl.formatMessage(messages.accountRequiredError)),
        }),
        {}
      )
    ),
  })

  // The form state is the "deep copy" of the immutable row entity — unsaved
  // changes never mutate the list (BHV-015)
  const formik = useFormik<CorrectExpenseItemAccountCodesFormSchema>({
    initialValues: {
      primaryCategory: item.account.secondaryCategory?.primaryCategory ?? null,
      secondaryCategory: item.account.secondaryCategory,
      applyToAllAccount: null,
      accountsByItemId: rows.reduce<Record<number, Nullable<Account>>>(
        (map, row) => ({ ...map, [row.id]: row.account }),
        {}
      ),
    },
    enableReinitialize: true,
    validationSchema,
    // Submits directly with NO confirmation dialog (BHV-021); the async save
    // is tracked by isSaving, so isSubmitting is released immediately
    onSubmit: (submittedValues, { setSubmitting }) => {
      onSubmit(submittedValues)
      setSubmitting(false)
    },
  })

  const { values, setFieldValue } = formik

  const secondaryCategoryMissing = !values.secondaryCategory

  const clearAllAccounts = () => {
    setFieldValue(
      'accountsByItemId',
      rows.reduce<Record<number, Nullable<Account>>>(
        (map, row) => ({ ...map, [row.id]: null }),
        {}
      )
    )
    setFieldValue('applyToAllAccount', null)
  }

  const handlePrimaryCategoryChange = (option: Nullable<PrimaryCategory>) => {
    // PrimaryCategory change clears the SecondaryCategory (BHV-018)
    setFieldValue('primaryCategory', option)
    handleSecondaryCategoryChange(null)
  }

  const handleSecondaryCategoryChange = (
    option: Nullable<SecondaryCategory>
  ) => {
    // SecondaryCategory change clears every account selection and expands the
    // siblings section (BHV-018)
    setFieldValue('secondaryCategory', option)
    clearAllAccounts()
    setShowSiblings(true)
  }

  const handleApplyToAllAccountChange = (option: Nullable<Account>) => {
    // APPLY-TO-ALL fans out to the primary item and every sibling and
    // triggers per-item budget-period lookups (BHV-019)
    setFieldValue('applyToAllAccount', option)
    setFieldValue(
      'accountsByItemId',
      rows.reduce<Record<number, Nullable<Account>>>(
        (map, row) => ({ ...map, [row.id]: option }),
        {}
      )
    )

    if (option) {
      rows.forEach((row) => onAccountSelected(row.id, option.id))
    }
  }

  const handleRowAccountChange = (
    row: ApprovedExpenseItem,
    option: Nullable<Account>
  ) => {
    setFieldValue('applyToAllAccount', null)
    setFieldValue(`accountsByItemId.${row.id}`, option)

    if (option) {
      onAccountSelected(row.id, option.id)
    }
  }

  const renderBudgetDate = (row: ApprovedExpenseItem) => {
    const budgetPeriod = budgetPeriodsByItemId[row.id]

    if (!values.accountsByItemId[row.id] || !budgetPeriod) {
      return intl.formatMessage(messages.budgetDateEmpty)
    }

    return intl.formatMessage(messages.budgetDateValue, {
      status: intl.formatMessage(
        budgetPeriod.expired
          ? messages.budgetStatusExpired
          : messages.budgetStatusActive
      ),
      startDate: intl.formatDate(budgetPeriod.startDate),
      endDate: intl.formatDate(budgetPeriod.endDate),
    })
  }

  const buildColumns = (): (
    | DatatableColumnConfig<'BASIC', ApprovedExpenseItem>
    | DatatableColumnConfig<'PRICE', ApprovedExpenseItem>
  )[] => {
    const columns: (
      | DatatableColumnConfig<'BASIC', ApprovedExpenseItem>
      | DatatableColumnConfig<'PRICE', ApprovedExpenseItem>
    )[] = [
      col('BASIC', {
        label: intl.formatMessage(messages.columnExpenseNumber),
        getter: (row) => row.label,
      }),
      col('BASIC', {
        label: intl.formatMessage(messages.columnReceipt),
        getter: (row) =>
          row.attachment ? (
            <a
              href={resolveAttachmentUrl(row.attachment)}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={intl.formatMessage(messages.receiptLinkAriaLabel, {
                label: row.label,
              })}
            >
              {getAttachmentFileType(row.attachment)}
            </a>
          ) : null,
      }),
      col('BASIC', {
        label: intl.formatMessage(messages.columnType),
        getter: (row) => row.expenseTypeLabel,
      }),
      col('PRICE', {
        label: intl.formatMessage(messages.columnTotalAmount),
        getter: (row) => ({
          price: row.amount,
          // Sibling rows share the report-level currency
          currencyName: currencyCode,
        }),
      }),
      col('BASIC', {
        label: intl.formatMessage(messages.columnAccountCode),
        width: 240,
        getter: (row) => (
          <AccountSelect
            aria-label={`${intl.formatMessage(
              messages.columnAccountCode
            )} ${row.label}`}
            secondaryCategoryId={values.secondaryCategory?.id}
            value={values.accountsByItemId[row.id]}
            isDisabled={secondaryCategoryMissing || isSaving}
            onChange={(option: Nullable<Account>) =>
              handleRowAccountChange(row, option)
            }
          />
        ),
        decorate: (cell: ReactNode) => (
          <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
        ),
      }),
    ]

    if (showBudgetDate) {
      // Budget Date column hidden under enable_flexible_spend_tracking
      // (BHV-020)
      columns.push(
        col('BASIC', {
          label: intl.formatMessage(messages.columnBudgetDate),
          getter: (row) => renderBudgetDate(row),
        })
      )
    }

    return columns
  }

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space size='large'>
          <Space direction='horizontal' align='start'>
            <Space.Item width={1 / 4}>
              <Descriptions columns={1}>
                <Descriptions.Item
                  label={intl.formatMessage(messages.requesterLabel)}
                >
                  {item.requesterFullName}
                </Descriptions.Item>
                {/* Angular shows CREATED/MODIFIED, but the v3 approved
                    endpoint carries no modified date — mirror of 2.1's
                    accepted Requester/Submitted/Approved summary so both
                    tabs' modals read identically and labels match the data */}
                <Descriptions.Item
                  label={intl.formatMessage(messages.submittedLabel)}
                >
                  {item.submittedDate
                    ? intl.formatDate(item.submittedDate)
                    : null}
                </Descriptions.Item>
                <Descriptions.Item
                  label={intl.formatMessage(messages.approvedLabel)}
                >
                  {item.approvedDate
                    ? intl.formatDate(item.approvedDate)
                    : null}
                </Descriptions.Item>
              </Descriptions>
            </Space.Item>
            <Space.Item width={1 / 4}>
              <Field
                label={intl.formatMessage(
                  messages.applyToAllPrimaryCategoryLabel
                )}
                labelFor='correct-expense-item-primary-category'
              >
                <PrimaryCategorySelect
                  inputId='correct-expense-item-primary-category'
                  value={values.primaryCategory}
                  onChange={handlePrimaryCategoryChange}
                />
              </Field>
            </Space.Item>
            <Space.Item width={1 / 4}>
              <Field
                label={intl.formatMessage(
                  messages.applyToAllSecondaryCategoryLabel
                )}
                labelFor='correct-expense-item-secondary-category'
                error={secondaryCategoryMissing}
                helperText={
                  secondaryCategoryMissing
                    ? intl.formatMessage(
                        messages.secondaryCategoryRequiredError
                      )
                    : undefined
                }
              >
                <SecondaryCategorySelect
                  inputId='correct-expense-item-secondary-category'
                  key={values.primaryCategory?.id}
                  primaryCategoryId={values.primaryCategory?.id}
                  value={values.secondaryCategory}
                  isClearable
                  onChange={handleSecondaryCategoryChange}
                />
              </Field>
            </Space.Item>
            {showApplyToAllAccount && (
              // Hidden under enable_flexible_spend_tracking (BHV-020)
              <Space.Item width={1 / 4}>
                <Field
                  label={intl.formatMessage(messages.applyToAllAccountLabel)}
                >
                  <AccountSelect
                    aria-label={intl.formatMessage(
                      messages.applyToAllAccountLabel
                    )}
                    secondaryCategoryId={values.secondaryCategory?.id}
                    value={values.applyToAllAccount}
                    isDisabled={secondaryCategoryMissing || isSaving}
                    onChange={handleApplyToAllAccountChange}
                  />
                </Field>
              </Space.Item>
            )}
          </Space>

          <DataTable<ApprovedExpenseItem>
            id='correct-expense-item-primary'
            aria-label={intl.formatMessage(messages.primaryItemTableAriaLabel)}
            data={[item]}
            columns={buildColumns()}
            customizableColumns={false}
            persistCustomizedColumns={false}
          />

          {item.expenseReport.hasSiblingItems && (
            <Collapsible
              open={showSiblings}
              onChange={() => setShowSiblings((previous) => !previous)}
              title={
                <Space
                  direction='horizontal'
                  justify='space-between'
                  align='center'
                >
                  <Text bold>
                    {intl.formatMessage(messages.siblingSectionTitle, {
                      reportNumber: item.expenseReport.id,
                      count: siblings.length,
                    })}
                  </Text>
                  <Space direction='horizontal' size='small' align='center'>
                    <IconAttention size={16} />
                    <Text bold>
                      {intl.formatMessage(messages.siblingAttentionNote)}
                    </Text>
                  </Space>
                </Space>
              }
            >
              <DataTable<ApprovedExpenseItem>
                id='correct-expense-item-siblings'
                aria-label={intl.formatMessage(
                  messages.siblingItemsTableAriaLabel
                )}
                data={siblings}
                isLoading={isLoadingSiblings}
                columns={buildColumns()}
                customizableColumns={false}
                persistCustomizedColumns={false}
                hideHeader
              />
            </Collapsible>
          )}

          <Divider />
          <Space direction='horizontal' justify='end' align='center'>
            <TertiaryButton
              type='button'
              onClick={onCancel}
              disabled={isSaving}
            >
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.saveChangesButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CorrectExpenseItemAccountCodesForm.displayName =
  'CorrectExpenseItemAccountCodesForm'
